// src/utils/seasonScope.js — Pass 47C §2/§4/§8/§16: ONE season-scoping layer so every
// operations view derives its bookings, roster, dates and metrics from the SELECTED
// season — never from "all current members" or all-seasons aggregates.
import { deriveSeasonId } from './scheduleModel';
import { resolveInstructorCategory, resolveCurrentTeamState, TEAM_STATE, CURRENT_SEASON_ID, isOperationalStaff } from './instructorCategory';
import { isPositivelyUnpaid, normalizePaymentState, PAYMENT } from './paymentSemantics';
import { opsDateKey } from './opsTime';

const ms = (v) => (v?.toDate ? v.toDate().getTime() : v?._seconds ? v._seconds * 1000 : (v ? new Date(v).getTime() : null));

// Bookings in a given season (uses explicit seasonId, falls back to deriveSeasonId).
export function scopeToSeason(bookings = [], seasonId) {
  return bookings.filter((b) => (b.seasonId || deriveSeasonId(b)) === seasonId);
}

// §16 THE roster selector. Historical seasons → people with that season's membership
// PLUS anyone who has a booking that season (deduped). Current season (26/27) →
// confirmed-current + roster-review (never previous_staff, never arbitrary members).
export function getMembersForSeason(members = [], bookings = [], seasonId) {
  const seasonBookings = scopeToSeason(bookings, seasonId);
  const bookedIds = new Set(seasonBookings.map((b) => b.instructorId).filter(Boolean));
  const isCurrent = seasonId === CURRENT_SEASON_ID;
  const out = [];
  const seen = new Set();
  for (const m of members) {
    // Pass 58G2 — exclude EXPLICIT test/dev members (canonical `testAccount:true` / rosterSource 'test')
    // from the production operational roster selector, so they never inflate Overview/Team/Calendar/Day.
    // Marker-based only — NEVER filter by display-name string.
    if (m.testAccount === true || m.rosterSource === 'test') continue;
    if (!isOperationalStaff(m)) continue; // Pass 49A §11: operational people, not just teaching instructors
    const sm = m.seasonMemberships && m.seasonMemberships[seasonId];
    // Pass 48 §10: season membership counts if it has a category OR an explicit participation.
    const hasSeasonMembership = !!(sm && (sm.category || sm.participation));
    let include;
    if (isCurrent) include = resolveCurrentTeamState(m, seasonId) !== TEAM_STATE.PREVIOUS && (hasSeasonMembership || resolveCurrentTeamState(m, seasonId) === TEAM_STATE.CURRENT || resolveCurrentTeamState(m, seasonId) === TEAM_STATE.REVIEW);
    else include = hasSeasonMembership || bookedIds.has(m.userId);
    if (include && !seen.has(m.userId)) { seen.add(m.userId); out.push(m); }
  }
  return out;
}

// §3 split teaching instructors from other/non-teaching staff (e.g. drivers).
export function splitTeachingStaff(members = []) {
  const teaching = [], other = [];
  for (const m of members) (m.isTeachingInstructor === false || m.jobFunction === 'driver' ? other : teaching).push(m);
  return { teaching, other };
}

// §4 the actual first/last canonical event date for a season → the default date window.
export function seasonDateRange(bookings = [], seasonId) {
  const times = scopeToSeason(bookings, seasonId).map((b) => ms(b.startAt) || (b.serviceDate ? new Date(`${b.serviceDate}T00:00:00+09:00`).getTime() : null)).filter(Boolean).sort((a, b) => a - b);
  if (!times.length) return null;
  return { start: new Date(times[0]), end: new Date(times[times.length - 1]) };
}

// §5 season-aware "next scheduled day": first booking day within the season on/after `from`,
// else the season's first day (so a historical season never lands on today). Booking days are
// the canonical Asia/Tokyo calendar day — a 02:00 JST booking (17:00 UTC previous day) must
// resolve to its JST day, NOT the UTC day. `from` is also normalised to its JST day-key.
export function nextScheduledDayInSeason(bookings = [], seasonId, from = null) {
  const days = [...new Set(scopeToSeason(bookings, seasonId).map((b) => (b.startAt ? opsDateKey(b.startAt) : b.serviceDate)).filter(Boolean))].sort();
  if (!days.length) return null;
  if (from) { const f = opsDateKey(from) || from; const next = days.find((d) => d >= f); if (next) return next; }
  return days[0];
}

// §6/§8 season-scoped metrics. Unpaid uses POSITIVE evidence only (missing/legacy ≠ unpaid).
export function seasonMetrics(bookings = [], members = [], seasonId) {
  const sb = scopeToSeason(bookings, seasonId).filter((b) => b.status !== 'canceled');
  const roster = getMembersForSeason(members, bookings, seasonId);
  // Pass 49A §14: explicit unpaid (positive evidence) is DISTINCT from not-recorded
  // (missing / legacy hardcoded 'unpaid'). Both derived via the one payment normaliser.
  const positivelyUnpaid = sb.filter(isPositivelyUnpaid).length;
  const paymentNotRecorded = sb.filter((b) => normalizePaymentState(b.paymentState ?? b.paymentStatus) === PAYMENT.NOT_RECORDED).length;
  const school = roster.filter((m) => resolveInstructorCategory(m, { season: seasonId }) === 'school').length;
  const collective = roster.filter((m) => resolveInstructorCategory(m, { season: seasonId }) === 'collective').length;
  return { seasonId, totalBookings: sb.length, roster: roster.length, school, collective, unpaid: positivelyUnpaid, paymentNotRecorded };
}
